import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import {
  AuthGuard,
  // StepType,
  MemberResolver,
  MemberCardResolver,
  BalanceResolver,
  CardTokenResolver,
  MaintenanceGuard,
  // FGBPreLoadingStrategy,
  firstTimeUserGuard,
} from "@fgb/core";

// General
import { MasterPageComponent } from "./pages/master/master-page/master-page.component";
import { HomePageComponent } from "./pages/home-page/home-page.component";
import { TermsPageComponent } from "./pages/terms-page/terms-page.component";
import { NotFoundPageComponent } from "./pages/not-found-page/not-found-page.component";
// Loyalty
import { RewardsLayoutComponent } from "./pages/rewards-layout/rewards-layout.component";
import { WalletPageComponent } from "./pages/wallet-page/wallet-page.component";
import { LottoWalletDetailsPageComponent } from "./pages/lotto-wallet-details-page/lotto-wallet-details-page.component";

// Account
import { AccountDetailsPageComponent } from "./pages/account-details-page/account-details-page.component";
// Ecash
import { EcashPageComponent } from "./pages/ecash-page/ecash-page.component";
import { EcashSuccessPageComponent } from "./pages/ecash-page/ecash-success-page/ecash-success-page.component";
import { EcashFailurePageComponent } from "./pages/ecash-page/ecash-failure-page/ecash-failure-page.component";
import { EcashResultPageComponent } from "./pages/ecash-page/ecash-result-page/ecash-result-page.component";
import { EcashHistoryPageComponent } from "./pages/ecash-history/ecash-history.component";

// Unauthenticated
import { AuthCallbackComponent } from "./modules/user/components/auth/auth-callback/auth-callback.component";
import { LoginPageComponent } from "./pages/login-page/login-page.component";
import { SingleSignOnComponent } from "./modules/user/components/single-sign-on/single-sign-on.component";
import { MaintenancePageComponent } from "./pages/maintenance-page/maintenance-page.component";
// Club Pages
import { TmCallbackComponent } from "./shared/components/manage-tickets/tm-callback/tm-callback.component";
import { TmAuthCallbackComponent } from "./shared/components/providers/tm/tm-auth-callback/tm-auth-callback.component";
import { V1SingleSignOnComponent } from "./modules/user/components/v1-single-sign-on/v1-single-sign-on.component";
// Campaign Pages
import { CampaignV2DetailsOfferPageComponent } from "./pages/campaign-v2-details-offer-page/campaign-v2-details-offer-page.component";
import { CampaignV2DetailsVoucherPageComponent } from "./pages/campaign-v2-details-voucher-page/campaign-v2-details-voucher-page.component";
// Offer Pages
import { PaymentPageComponent } from "./pages/payment-page/payment-page.component";
import { PaymentLayoutComponent } from "./pages/payment-layout/payment-layout.component";
import { ProviderCallbackComponent } from "./shared/components/providers/provider-callback/provider-callback.component";
import { RegistrationLayoutPageComponent } from "./modules/registration/pages/registration-layout-page/registration-layout-page.component";
import { WalletClaimedPageComponent } from "./pages/wallet-claimed-page/wallet-claimed-page.component";
import { provideRegistrationConfig } from "@fgb/portal-component-library/src/lib/registration";
import { MarketplaceWalletPageComponent } from "./pages/marketplace-wallet-page/marketplace-wallet-page.component";

const routes: Routes = [
  {
    path: "",
    canActivate: [AuthGuard, firstTimeUserGuard()],
    canActivateChild: [MaintenanceGuard],
    component: MasterPageComponent,
    resolve: {
      member: MemberResolver,
      memberCard: MemberCardResolver,
      balances: BalanceResolver,
      cardToken: CardTokenResolver,
    },
    children: [
      {
        path: "",
        component: HomePageComponent,
      },

      {
        path: "rewards",
        component: RewardsLayoutComponent,
        children: [
          {
            path: "wallet",
            children: [
              {
                path: "",
                component: WalletPageComponent,
              },
              {
                path: "details/:id",
                component: MarketplaceWalletPageComponent,
              },
              {
                path: "raffledetails/:id",
                component: LottoWalletDetailsPageComponent,
              },
            ],
          },

          {
            path: "claimed",
            children: [
              {
                path: "",
                component: WalletClaimedPageComponent,
              },
            ],
          },
        ],
      },
      {
        path: "account",
        resolve: {
          member: MemberResolver,
        },
        children: [
          {
            path: "",
            component: AccountDetailsPageComponent,
          },
        ],
      },
      {
        path: "payment",
        component: PaymentLayoutComponent,
        resolve: { balances: BalanceResolver, cardTokens: CardTokenResolver },
        children: [
          { path: "", component: PaymentPageComponent },
          { path: "history", component: EcashHistoryPageComponent },
        ],
      },

      {
        path: "ecash",
        resolve: { balances: BalanceResolver, cardTokens: CardTokenResolver },
        children: [
          { path: "", component: EcashPageComponent },
          {
            path: "success",
            resolve: { balances: BalanceResolver },
            component: EcashSuccessPageComponent,
          },
          { path: "failure", component: EcashFailurePageComponent },
          { path: "result", component: EcashResultPageComponent },
        ],
      },
      {
        path: "cardtoken",
        children: [
          { path: "success", component: EcashSuccessPageComponent },
          { path: "failure", component: EcashFailurePageComponent },
          { path: "result", component: EcashResultPageComponent },
        ],
      },
      {
        path: "campaign-v2",
        children: [
          {
            path: "details/offer/:offerId",
            component: CampaignV2DetailsOfferPageComponent,
          },
          {
            path: "details/voucher/:voucherId",
            component: CampaignV2DetailsVoucherPageComponent,
          },
        ],
      },
      { path: "terms", component: TermsPageComponent },

      { path: "404page", component: NotFoundPageComponent },
      { path: "signin-ticketmaster", component: TmCallbackComponent },
      {
        path: "provider/:providerid",
        component: ProviderCallbackComponent,
      },
    ],
  },

  {
    path: "login",
    redirectTo: "",
    component: LoginPageComponent,
    canActivate: [MaintenanceGuard],
  },

  {
    path: "register",
    component: RegistrationLayoutPageComponent,
    loadChildren: () =>
      import("@fgb/portal-component-library/src/lib/registration").then(
        (m) => m.RegistrationModule
      ),
    providers: [provideRegistrationConfig()],
  },
  { path: "callback", component: AuthCallbackComponent },
  { path: "sso", component: SingleSignOnComponent },
  { path: "login/singleSignOn/:providerId", component: SingleSignOnComponent },
  {
    path: "login/singleSignOn/:providerId/:clubId",
    component: SingleSignOnComponent,
  },
  { path: "login/ss/login.aspx", component: V1SingleSignOnComponent },
  { path: "maintenance", component: MaintenancePageComponent },
  { path: "tm-auth-callback", component: TmAuthCallbackComponent },
  { path: "**", redirectTo: "404page" },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: "enabled",
      // preloadingStrategy: FGBPreLoadingStrategy,
      bindToComponentInputs: true,
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
